import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SiteProvider } from "@/context/SiteContext";
import { useLang } from "@/lib/i18n";
import { Header, Footer } from "@/components/site/Chrome";
import { Consultant } from "@/components/site/Consultant";
import Home from "@/pages/site/Home";
import Catalog from "@/pages/site/Catalog";
import WatchPage from "@/pages/site/WatchPage";
import NotFound from "@/pages/site/NotFound";
import { Orders, Contact, Legal, Track } from "@/pages/site/Pages";
import { AuthProvider, RequireAuth, AdminLogin } from "@/pages/admin/AdminAuth";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Watches from "@/pages/admin/Watches";
import WatchEdit from "@/pages/admin/WatchEdit";
import Brands from "@/pages/admin/Brands";
import Stock from "@/pages/admin/Stock";
import Report from "@/pages/admin/Report";
import { Requests, RequestDetail } from "@/pages/admin/Requests";
import { Clients, ClientDetail } from "@/pages/admin/Clients";
import ConsultantAdmin from "@/pages/admin/ConsultantAdmin";
import { Telegram, Settings, Users, Perfil } from "@/pages/admin/AdminMisc";
import WhatsAppAdmin from "@/pages/admin/WhatsAppAdmin";
import Reviews from "@/pages/admin/Reviews";
import Interest from "@/pages/admin/Interest";
import Shares from "@/pages/admin/Shares";
import AccessLog from "@/pages/admin/AccessLog";
import { NoIndex } from "@/components/NoIndex";
import Review from "@/pages/site/Review";

function SiteLayout() {
  const lang = useLang();
  const { pathname } = useLocation();
  useEffect(() => { document.documentElement.lang = lang === "en" ? "en" : "pt-PT"; }, [lang]);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    // Record a public page view (anonymous). Fire-and-forget.
    import("@/lib/api").then(({ api }) => api.post("/public/pageview", { path: pathname, lang }).catch(() => {}));
  }, [pathname, lang]);
  return (
    <SiteProvider>
      <div className="min-h-screen bg-[#0C0B09]">
        <Header />
        <main className="pt-[72px]"><Outlet /></main>
        <Footer />
        <Consultant />
      </div>
    </SiteProvider>
  );
}

const siteRoutes = (p, e) => (
  <>
    <Route path={p.home} element={<Home />} />
    <Route path={p.catalog} element={<Catalog />} />
    <Route path={`${p.watch}/:slug`} element={<WatchPage />} />
    <Route path={p.orders} element={<Orders />} />
    <Route path={p.contact} element={<Contact />} />
    <Route path={p.privacy} element={<Legal kind="privacy" />} />
    <Route path={p.terms} element={<Legal kind="terms" />} />
    <Route path={p.track} element={<Track />} />
    <Route path={`${p.review}/:token`} element={<Review />} />
    {e && <Route path="/en/*" element={<NotFound />} />}
  </>
);

export default function App() {
  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
      <Routes>
        <Route path="/admin/*" element={<NoIndex><AuthProvider><Outlet /></AuthProvider></NoIndex>}>
          <Route path="login" element={<AdminLogin />} />
          <Route element={<RequireAuth><AdminLayout /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="relogios" element={<Watches />} />
            <Route path="relogios/:id" element={<WatchEdit />} />
            <Route path="marcas" element={<Brands />} />
            <Route path="stock" element={<Stock />} />
            <Route path="relatorio" element={<Report />} />
            <Route path="opinioes" element={<Reviews />} />
            <Route path="interesse" element={<Interest />} />
            <Route path="partilhas" element={<Shares />} />
            <Route path="pedidos" element={<Requests />} />
            <Route path="pedidos/:id" element={<RequestDetail />} />
            <Route path="clientes" element={<Clients />} />
            <Route path="clientes/:key" element={<ClientDetail />} />
            <Route path="consultor" element={<ConsultantAdmin />} />
            <Route path="definicoes" element={<Settings />} />
            <Route path="perfil" element={<Perfil />} />
            <Route path="integracoes" element={<RequireAuth superOnly><Telegram /></RequireAuth>} />
            <Route path="whatsapp" element={<RequireAuth adminOnly><WhatsAppAdmin /></RequireAuth>} />
            <Route path="utilizadores" element={<RequireAuth adminOnly><Users /></RequireAuth>} />
            <Route path="registo-acessos" element={<RequireAuth><AccessLog /></RequireAuth>} />
          </Route>
        </Route>
        <Route element={<SiteLayout />}>
          {siteRoutes({ home: "/", catalog: "/catalogo", watch: "/relogio", orders: "/encomendas", contact: "/contacto", privacy: "/privacidade", terms: "/termos", track: "/acompanhar", review: "/avaliacao" })}
          {siteRoutes({ home: "/en", catalog: "/en/catalog", watch: "/en/watch", orders: "/en/orders", contact: "/en/contact", privacy: "/en/privacy", terms: "/en/terms", track: "/en/track", review: "/en/review" }, true)}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
